import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ISupplier } from '../types';
import Icon from '../../../generic-components/Icon';
import SearchIcon from '../../../assets/svg/search-app-center-icon.svg';
import AddPayeeIcon from '../../../assets/svg/add-payee.svg';
import { ReactComponent as EditIcon } from '../../../assets/svg/edit-pen.svg';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/svg/shared/ic-shared-right-arrow.svg';
import Heading from '../../../generic-components/Heading';
import Button from '../../../generic-components/Button';
import { VIEWS_MAP } from '../../../router/constants';
import { getAddressDetails } from '../helpers';
import { DYNAMIC_NAVIGATION_TITLE_MAP, DYNAMIC_TITLE_MAP, STYLES, SUPPLIER_REFERRER_TYPES } from '../consts';
import { ReactComponent as AddressIcon } from '../../../assets/svg/location-pin.svg';
import { STEPS } from '../../MailCheck/consts';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { STEPS_MAP } from '../../BankTransfer/constants';
import { Avatar, AvatarSize } from '../../../DesignSystem/VendorProfile/Avatar';

interface IProps {
  list: ISupplier[];
  referrer: string;
  desireStep: string;
}

export const PickerWrapper = ({ list, referrer, desireStep }: IProps) => {
  const [keyword, setKeyword] = useState<string>('');
  const navigate = useNavigate();
  const { state: routeState } = useLocation();
  const { t } = useTranslation();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => setKeyword(e.target.value);

  const filteredList = useMemo(
    () => list.filter((i) => i.name.toLowerCase().includes(keyword.toLowerCase())),
    [keyword],
  );

  const pickCallbackMap = {
    [SUPPLIER_REFERRER_TYPES.MAIL_CHECK]: (payee: ISupplier) => {
      if (!payee?.supplierAddressData?.line1) {
        navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.MANAGE.BASE}/${payee?.supplierId}`, {
          state: {
            pageTitle: 'Mail a paper check',
            formHeader: 'Enter recipient\'s details',
            formType: 'MAIL_A_CHECK',
            onExitDestination: `/${VIEWS_MAP.DASHBOARD}`,
            isUpdateOnlyMode: true,
            isNativeBack: true,
          },
        });
      } else {
        navigate(`/${VIEWS_MAP.MAIL_A_CHECK}`, {
          state: {
            redirectStep: desireStep || STEPS.AMOUNT,
            payeeDetails: payee,
          },
        });
      }
    },
    [SUPPLIER_REFERRER_TYPES.ACH_OUT]: (payee: ISupplier) => {
      navigate(`/${VIEWS_MAP}`, {
        replace: true,
        state: {
          redirectStep: STEPS_MAP.TRANSFER_AMOUNT,
          payeeDetails: payee,
          transferAmount: routeState?.transferAmount || '',
        },
      });
    },
    [SUPPLIER_REFERRER_TYPES.ACH_OUT_MANDATORY]: (payee: ISupplier) => {
      navigate(`/${VIEWS_MAP}`, {
        state: {
          redirectStep: STEPS_MAP.TRANSFER_AMOUNT,
          payeeDetails: payee,
        },
      });
    },
  };

  const addNewPayee = () => {
    trackMixPanel('Act on Payee Addressbook Screen', { Action: 'Add New Payee' });
    navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.MANAGE.BASE}`, {
      state: {
        pageTitle: DYNAMIC_NAVIGATION_TITLE_MAP[referrer],
        formHeader: DYNAMIC_TITLE_MAP[referrer],
        formType: referrer,
        onExitDestination: `/${VIEWS_MAP.DASHBOARD}`,
        isNativeBack: true,
      },
    });
  };

  const handleNewPayeeFromSearch = () => {
    trackMixPanel('Act on Payee Addressbook Screen', { Action: 'Add New Payee from Search' });
    navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.MANAGE.BASE}`, {
      state: {
        preFilledName: keyword,
        pageTitle: DYNAMIC_NAVIGATION_TITLE_MAP[referrer],
        formHeader: DYNAMIC_TITLE_MAP[referrer],
        formType: referrer,
        onExitDestination: `/${VIEWS_MAP.DASHBOARD}`,
        isNativeBack: true,
      },
    });
  };

  const handlePayeePick = (payee: ISupplier) => {
    trackMixPanel('Act on Payee Addressbook Screen', { Action: 'Select Payee' });
    pickCallbackMap[referrer as keyof typeof pickCallbackMap](payee);
  };

  const handleEditPayee = (id: string) => {
    trackMixPanel('Act on Payee Addressbook Screen', { Action: 'Edit Payee' });
    navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.MANAGE.BASE}/${id}`, {
      state: {
        pageTitle: DYNAMIC_NAVIGATION_TITLE_MAP[referrer],
        formHeader: DYNAMIC_TITLE_MAP[referrer],
        formType: referrer,
        onExitDestination: `/${VIEWS_MAP.DASHBOARD}`,
        isNativeBack: true,
      },
    });
  };

  useEffect(() => {
    trackMixPanel('View Payee Addressbook Screen', {
      Origin: 'Menu',
      Flow: 'Mail a Check',
      'Number of vendor in Address Book': list.length,
    });
  }, []);

  return (
    <div className={STYLES.PICKER_COMPONENT_WRAPPER}>
      <div className={STYLES.FILTER_LIST_WRAPPER}>
        <Icon className={STYLES.FILTER_INPUT_ICON} src={SearchIcon} />
        <input
          id='searchInput'
          maxLength={30}
          className={STYLES.FILTER_INPUT}
          placeholder={t('SUPPLIER.PICKER.SEARCH_PLACEHOLDER')}
          type='text'
          onChange={handleChange}
          value={keyword}
        />
      </div>

      <Button
        onClick={addNewPayee}
        className='w-full'
        id='addNewPayee'
        childElement={
          <div className={STYLES.PICKER.ADD_NEW_PAYEE.WRAPPER}>
            <Icon className={STYLES.PICKER.ADD_NEW_PAYEE.ICON} src={AddPayeeIcon} />
            <Heading title={t('SUPPLIER.PICKER.ADD_PAYEE_TITLE')} className={STYLES.PICKER.ADD_NEW_PAYEE.TITLE} />
            <ArrowIcon className={STYLES.PICKER.ADD_NEW_PAYEE.ARROW} />
          </div>
        }
      />

      <Heading title={t('SUPPLIER.PICKER.PAYEE_LIST_TITLE')} className={STYLES.PICKER.LIST.TITLE} />

      <div className={STYLES.PICKER.LIST.WRAPPER_PLACEHOLDER}>
        {!!filteredList.length && (
          <div className={STYLES.PICKER.LIST.WRAPPER_SCROLLABLE}>
            {filteredList.map((i) => (
              <React.Fragment key={i.supplierId}>
                <Button
                  onClick={() => handlePayeePick(i)}
                  className={STYLES.PICKER.LIST.PAYEE_BUTTON}
                  id={`payeeItem-${i.supplierId}`}
                  childElement={
                    <div className={STYLES.PICKER.LIST.PAYEE_PREVIEW_WRAPPER}>
                      <Avatar name={i.name} id={i.supplierId || ''} size={AvatarSize.LARGE} />
                      <div className={STYLES.PICKER.LIST.PREVIEW_NAME_AND_ADDRESS_WRAPPER}>
                        <Heading title={i.name} className={STYLES.PICKER.LIST.PAYEE_NAME} />
                        {i?.supplierAddressData?.line1 && (
                          <div className={STYLES.PICKER.LIST.PAYEE_ADDRESS_CONTAINER}>
                            <AddressIcon className={STYLES.PICKER.LIST.PAYEE_ADDRESS_ICON} />
                            <Heading
                              title={getAddressDetails(i.supplierAddressData)}
                              className={STYLES.PICKER.LIST.PAYEE_ADDRESS_TITLE}
                            />
                          </div>
                        )}
                      </div>
                      <Button
                        onClick={() => handleEditPayee(i?.supplierId || '')}
                        className={STYLES.PICKER.LIST.PAYEE_EDIT_BUTTON}
                        id={`editPayee-${i.supplierId}`}
                        childElement={<EditIcon className={STYLES.PICKER.LIST.PAYEE_EDIT_ICON} />}
                      />
                    </div>
                  }
                />
              </React.Fragment>
            ))}
          </div>
        )}
        {!filteredList.length && keyword && (
          <div className={STYLES.PICKER.LIST.ADD_PAYEE_FROM_KEYWORD_WRAPPER}>
            <Button
              onClick={handleNewPayeeFromSearch}
              title={`${t('SUPPLIER.PICKER.ADD_PAYEE_FROM_SEARCH')} ${keyword ? `" ${keyword} "` : ''}`}
              className={STYLES.PICKER.LIST.ADD_PAYEE_FROM_KEYWORD_BUTTON}
              id='addPayeeFromList'
            />
          </div>
        )}
      </div>
    </div>
  );
};
